import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Loader } from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { updateBusinessProfile } from "../../../../../services/profile";
import { showError, showSuccess } from "../../../../../utils";
import { uploadFileToS3Api } from "../../../../../utils/PresignedUrlFunction";
import { useAppTheme } from "../../../../../config/appTheme";
import { THEME_PRESETS } from "../../../../../config/themePresets";

/**
 * WhiteLabelPanel — redesign "White label" settings panel.
 *
 * Top-tier (Gold) plan only; the SettingsShell hides the nav item otherwise.
 * Lets an agency rebrand the experience: brand name + logo, primary/accent
 * colors, a sign-in background, and an option to hide Ogiso chrome branding.
 *
 * Persists to BusinessProfile.whiteLabel via PUT /me/business (the existing
 * updateBusinessProfile does Object.assign, so sending just { whiteLabel }
 * leaves the rest of the profile untouched).
 *
 * Note: a "Powered by Ogiso" mark is always retained on customer-facing
 * generated reports, regardless of the Hide-branding toggle.
 */

const DEFAULT_PRIMARY = "#FFBA45";
const DEFAULT_ACCENT = "#171717";
// Active module (sidebar) item — defaults to the standard black active state.
const DEFAULT_MODULE = "#171717";
// Sidebar menu hover highlight (matches the default Mantine gray hover).
const DEFAULT_HOVER = "#F8F9FA";

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const normHex = (v, fallback) => (HEX_RE.test(String(v || "").trim()) ? v.trim() : fallback);

/* Swatch + hex text + optional caption, no extra deps. */
function ColorField({ label, value, onChange, fallback, hint }) {
  const safe = normHex(value, fallback);
  return (
    <div className="wl-colorfield">
      <label className="label">{label}</label>
      <div className="wl-color">
        <input
          type="color"
          className="wl-color-swatch"
          value={safe}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
        />
        <input
          className="input"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={fallback}
          spellCheck={false}
        />
      </div>
      {hint ? <div className="field-hint" style={{ marginTop: 6 }}>{hint}</div> : null}
    </div>
  );
}

function Switch({ on, onClick, disabled }) {
  return <button type="button" className={`switch${on ? " on" : ""}`} onClick={onClick} aria-pressed={on} disabled={disabled} />;
}

// Collapsible settings card — header toggles the body. Defaults closed so the
// section loads compact; click a header to expand that group.
function Collapsible({ title, subtitle, defaultOpen = false, badge, onToggle, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && onToggle) onToggle();
  };
  return (
    <div className={`card wl-collapse${open ? " open" : ""}`}>
      <button type="button" className="wl-collapse-head" onClick={toggle} aria-expanded={open}>
        <div className="wl-collapse-titles">
          <div className="card-title">
            {title}
            {badge ? <span className="wl-soon">{badge}</span> : null}
          </div>
          {subtitle ? <div className="card-subtitle">{subtitle}</div> : null}
        </div>
        <svg className="wl-collapse-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open ? <div className="card-section wl-collapse-body">{children}</div> : null}
    </div>
  );
}

function CheckMark() {
  return (
    <span className="wl-theme-check" aria-hidden="true">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );
}

// Design settings are grouped into sections; each drives its own live preview.
const SECTIONS = [
  { key: "brand", label: "Brand & Theme" },
  { key: "advanced", label: "Advanced" },
];

// Readable ink (dark/light) for a chosen background color (YIQ contrast).
const inkOn = (hex) => {
  const h = String(hex || "").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) || 0);
  return (r * 299 + g * 587 + b * 114) / 1000 >= 140 ? "#171717" : "#FFFFFF";
};

export default function WhiteLabelPanel({ profile, role }) {
  const reduxProfile = useSelector((state) => state?.profile);
  const businessProfile = profile || reduxProfile?.profile;
  const business = businessProfile?.business || {};
  const wl = business?.whiteLabel || {};

  const userType = businessProfile?.user?.type ?? role;
  const isAdmin = userType !== "staff";

  // Theme preset (applies app-wide, independent of the custom brand colors).
  const [themeKey, setThemeKey] = useAppTheme();

  // ── State ──
  // Brand name + logo are the SAME fields as the Organization profile
  // (single source of truth): business.companyName / business.logo. The
  // whiteLabel record only holds the toggle + colors.
  const [enabled, setEnabled] = useState(!!wl.enabled);
  const [brandName, setBrandName] = useState(business.companyName || "");
  const [primaryColor, setPrimaryColor] = useState(wl.primaryColor || DEFAULT_PRIMARY);
  const [accentColor, setAccentColor] = useState(wl.accentColor || DEFAULT_ACCENT);
  // Module buttons (Add Staff/Client/Task etc.) follow the primary color
  // unless this sets them apart; the active sidebar item matches it too.
  const [moduleButtonColor, setModuleButtonColor] = useState(wl.moduleButtonColor || DEFAULT_MODULE);
  // Hover highlight for the sidebar menu items.
  const [hoverAccentColor, setHoverAccentColor] = useState(wl.hoverAccentColor || DEFAULT_HOVER);
  const [hideOgisoBranding, setHideOgisoBranding] = useState(!!wl.hideOgisoBranding);

  // Sign-in screen branding.
  const [loginBgType, setLoginBgType] = useState(wl.loginBgType || "color");
  const [loginBgColor, setLoginBgColor] = useState(wl.loginBgColor || DEFAULT_PRIMARY);
  const [loginTagline, setLoginTagline] = useState(wl.loginTagline || "");

  // Email sender identity + support contact.
  const [emailFromName, setEmailFromName] = useState(wl.emailFromName || "");
  const [emailReplyTo, setEmailReplyTo] = useState(wl.emailReplyTo || "");
  const [supportEmail, setSupportEmail] = useState(wl.supportEmail || "");
  const [supportPhone, setSupportPhone] = useState(wl.supportPhone || "");
  const [supportUrl, setSupportUrl] = useState(wl.supportUrl || "");

  // Which settings section is active (drives the fields).
  const [section, setSection] = useState("brand");
  // Which surface the live preview shows — follows the last-opened group.
  const [previewMode, setPreviewMode] = useState("app");
  const goSection = (key) => {
    setSection(key);
    setPreviewMode(key === "advanced" ? "signin" : "app");
  };

  const [saving, setSaving] = useState(false);

  // Logo uploads — full logo is also the Organization business logo; the mark
  // (collapsed nav + favicon), dark variant, and sign-in background live only
  // on the whiteLabel record. Explicit state so Reset can clear local edits.
  const logoRef = useRef();
  const [logoSrc, setLogoSrc] = useState(business.logo || "");
  const [logoUploading, setLogoUploading] = useState(false);
  const darkRef = useRef();
  const [logoDark, setLogoDark] = useState(wl.logoDark || "");
  const [darkUploading, setDarkUploading] = useState(false);
  const loginBgRef = useRef();
  const [loginBgImage, setLoginBgImage] = useState(wl.loginBgImage || "");
  const [loginBgUploading, setLoginBgUploading] = useState(false);

  const upload = async (file, setUploading, onUrl) => {
    if (!file) return;
    try {
      setUploading(true);
      const result = await uploadFileToS3Api({ file });
      onUrl(result?.uploadURL || "");
    } catch (err) {
      showError(err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (enabled && !brandName.trim()) {
      showError("Brand name is required to enable custom design");
      return;
    }
    try {
      setSaving(true);
      const whiteLabel = {
        enabled,
        logoDark,
        primaryColor: normHex(primaryColor, DEFAULT_PRIMARY),
        accentColor: normHex(accentColor, DEFAULT_ACCENT),
        moduleButtonColor: normHex(moduleButtonColor, DEFAULT_MODULE),
        hoverAccentColor: normHex(hoverAccentColor, DEFAULT_HOVER),
        hideOgisoBranding,
        loginBgType,
        loginBgColor: normHex(loginBgColor, DEFAULT_PRIMARY),
        loginBgImage,
        loginTagline: loginTagline.trim(),
        emailFromName: emailFromName.trim(),
        emailReplyTo: emailReplyTo.trim(),
        supportEmail: supportEmail.trim(),
        supportPhone: supportPhone.trim(),
        supportUrl: supportUrl.trim(),
      };
      // Brand name + logo write to the shared Organization fields so they're
      // used consistently across the app (nav, invoices, Daily Digest).
      await updateBusinessProfile({ companyName: brandName.trim(), logo: logoSrc, whiteLabel });
      showSuccess("Design settings saved");
    } catch (err) {
      showError(err?.message || "Could not save design settings");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEnabled(!!wl.enabled);
    setBrandName(business.companyName || "");
    setPrimaryColor(wl.primaryColor || DEFAULT_PRIMARY);
    setAccentColor(wl.accentColor || DEFAULT_ACCENT);
    setModuleButtonColor(wl.moduleButtonColor || DEFAULT_MODULE);
    setHoverAccentColor(wl.hoverAccentColor || DEFAULT_HOVER);
    setHideOgisoBranding(!!wl.hideOgisoBranding);
    setLogoSrc(business.logo || "");
    setLogoDark(wl.logoDark || "");
    setLoginBgType(wl.loginBgType || "color");
    setLoginBgColor(wl.loginBgColor || DEFAULT_PRIMARY);
    setLoginBgImage(wl.loginBgImage || "");
    setLoginTagline(wl.loginTagline || "");
    setEmailFromName(wl.emailFromName || "");
    setEmailReplyTo(wl.emailReplyTo || "");
    setSupportEmail(wl.supportEmail || "");
    setSupportPhone(wl.supportPhone || "");
    setSupportUrl(wl.supportUrl || "");
  };

  // Turn off custom design + reset colors and persist, so the app drops the
  // custom branding immediately. Brand name + logo are the org's identity
  // (used on invoices etc.), so they are NOT wiped here.
  const handleReset = async () => {
    setEnabled(false);
    setPrimaryColor(DEFAULT_PRIMARY);
    setAccentColor(DEFAULT_ACCENT);
    setModuleButtonColor(DEFAULT_MODULE);
    setHoverAccentColor(DEFAULT_HOVER);
    setHideOgisoBranding(false);
    setLogoDark("");
    setLoginBgType("color");
    setLoginBgColor(DEFAULT_PRIMARY);
    setLoginBgImage("");
    setLoginTagline("");
    setEmailFromName("");
    setEmailReplyTo("");
    setSupportEmail("");
    setSupportPhone("");
    setSupportUrl("");
    try {
      setSaving(true);
      await updateBusinessProfile({
        whiteLabel: {
          enabled: false,
          logoDark: "",
          primaryColor: DEFAULT_PRIMARY,
          accentColor: DEFAULT_ACCENT,
          moduleButtonColor: DEFAULT_MODULE,
          hoverAccentColor: DEFAULT_HOVER,
          hideOgisoBranding: false,
          loginBgType: "color",
          loginBgColor: DEFAULT_PRIMARY,
          loginBgImage: "",
          loginTagline: "",
          emailFromName: "",
          emailReplyTo: "",
          supportEmail: "",
          supportPhone: "",
          supportUrl: "",
        },
      });
      showSuccess("Reset to Ogiso branding");
    } catch (err) {
      showError(err?.message || "Could not reset design settings");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="settings-panel active" data-panel="white-label">
        <div className="page-header">
          <div>
            <div className="page-title">Design</div>
            <div className="page-subtitle">Rebrand the experience with your own identity.</div>
          </div>
        </div>
        <div className="card">
          <div className="card-section">
            <div className="card-subtitle">Only administrators can manage design settings.</div>
          </div>
        </div>
      </div>
    );
  }

  // Preview colors resolve to safe hex.
  const pPrimary = normHex(primaryColor, DEFAULT_PRIMARY);
  const pAccent = normHex(accentColor, DEFAULT_ACCENT);
  const pModuleInk = inkOn(normHex(moduleButtonColor, DEFAULT_MODULE));
  const displayBrand = brandName.trim() || "Your brand";

  return (
    <div className="settings-panel active" data-panel="white-label">
      <div className="page-header">
        <div>
          <div className="page-title">Design</div>
          <div className="page-subtitle">
            Rebrand the experience with your own name, logo, colors, and sign-in background.
          </div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn danger-ghost" onClick={handleReset} disabled={saving} title="Remove custom branding and restore Ogiso colors & logo">
            Reset
          </button>
          <button type="button" className="btn" onClick={handleCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn primary" onClick={handleSave} disabled={saving}>
            {saving ? <Loader size="xs" color="#171717" /> : "Save changes"}
          </button>
        </div>
      </div>

      {/* Master enable — always visible above the sections */}
      <div className="card wl-enable">
        <div className="card-section wl-row">
          <div>
            <div className="card-title">Enable custom design</div>
            <div className="card-subtitle">
              Apply your branding across the app. Available on the Gold plan.
            </div>
          </div>
          <Switch on={enabled} onClick={() => setEnabled((v) => !v)} />
        </div>
      </div>

      {/* Section tabs — group the settings and drive the contextual preview */}
      <div className="wl-tabs" role="tablist">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={section === s.key}
            className={`wl-tab${section === s.key ? " on" : ""}`}
            onClick={() => goSection(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="wl-grid">
        {/* ───────── Settings column ───────── */}
        <form className="wl-settings" onSubmit={handleSave}>
          {section === "brand" && (
          <>
          {/* Theme presets */}
          <Collapsible title="Theme" subtitle="Switch the whole app's look and feel. Applies instantly." defaultOpen onToggle={() => setPreviewMode("app")}>
              <div className="wl-theme-grid">
                {THEME_PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className={`wl-theme${themeKey === p.key ? " sel" : ""}`}
                    onClick={() => setThemeKey(p.key)}
                    title={p.label}
                  >
                    <span className="wl-theme-swatch" style={{ background: p.tokens.bg }}>
                      <span className="wl-theme-bar" style={{ background: p.tokens["sidebar-bg"] }} />
                      <span className="wl-theme-dot" style={{ background: p.tokens.primary }} />
                    </span>
                    <span className="wl-theme-name">{p.label}</span>
                    {themeKey === p.key ? <CheckMark /> : null}
                  </button>
                ))}
              </div>
          </Collapsible>

          {/* Brand identity */}
          <Collapsible title="Brand identity" subtitle="Your name and logo, shown across the white-labeled experience." onToggle={() => setPreviewMode("app")}>
              <div className="wl-brand">
                <div className="wl-brand-logo">
                  <Dropzone
                    openRef={logoRef}
                    onDrop={(files) => upload(files?.[0], setLogoUploading, setLogoSrc)}
                    accept={[MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.svg, MIME_TYPES.webp]}
                    multiple={false}
                    styles={{ root: { border: "none", padding: 0, background: "transparent" } }}
                  >
                    <div className="org-logo">
                      {logoUploading ? (
                        <Loader size="sm" color="#FFBA45" />
                      ) : logoSrc ? (
                        <img src={logoSrc} alt="Brand logo" />
                      ) : (
                        <span className="org-logo-empty">Logo</span>
                      )}
                    </div>
                  </Dropzone>
                  <div className="label" style={{ marginBottom: 0 }}>Brand logo</div>
                </div>
                <div className="wl-brand-actions">
                  <button type="button" className="btn sm" onClick={() => logoRef.current?.()} disabled={logoUploading}>
                    {logoSrc ? "Change logo" : "Upload logo"}
                  </button>
                  <div className="field-hint">PNG, JPG or SVG. Shown in the nav and sign-in screen.</div>
                </div>
              </div>

              <div className="wl-field">
                <label className="label">Brand name</label>
                <input
                  className="input"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="e.g. Acme Care"
                />
              </div>

              {/* Dark-theme logo variant. */}
              <div className="wl-logos">
                <div className="wl-logo-item">
                  <Dropzone
                    openRef={darkRef}
                    onDrop={(files) => upload(files?.[0], setDarkUploading, setLogoDark)}
                    accept={[MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.svg, MIME_TYPES.webp]}
                    multiple={false}
                    styles={{ root: { border: "none", padding: 0, background: "transparent" } }}
                  >
                    <div className="org-logo sm dark">
                      {darkUploading ? <Loader size="xs" color="#FFBA45" /> : logoDark ? <img src={logoDark} alt="Dark logo" /> : <span className="org-logo-empty">Dark</span>}
                    </div>
                  </Dropzone>
                  <div className="wl-logo-meta">
                    <div className="label" style={{ marginBottom: 2 }}>Dark logo</div>
                    <div className="field-hint">Shown on dark themes (Midnight).</div>
                    <div className="wl-logo-btns">
                      <button type="button" className="btn sm" onClick={() => darkRef.current?.()} disabled={darkUploading}>{logoDark ? "Change" : "Upload"}</button>
                      {logoDark ? <button type="button" className="btn sm ghost-danger" onClick={() => setLogoDark("")}>Remove</button> : null}
                    </div>
                  </div>
                </div>
              </div>
          </Collapsible>

          {/* Colors */}
          <Collapsible title="Colors" subtitle="Drive buttons, highlights, and active states across the app." onToggle={() => setPreviewMode("app")}>
              <div className="wl-color-grid">
                <ColorField label="Primary color" value={primaryColor} onChange={setPrimaryColor} fallback={DEFAULT_PRIMARY} hint="Main buttons and highlights" />
                <ColorField label="Accent color" value={accentColor} onChange={setAccentColor} fallback={DEFAULT_ACCENT} hint="Text and icons on primary" />
                <ColorField label="Module buttons color" value={moduleButtonColor} onChange={setModuleButtonColor} fallback={DEFAULT_MODULE} hint="Active menu item in the sidebar" />
                <ColorField label="Hover accent color" value={hoverAccentColor} onChange={setHoverAccentColor} fallback={DEFAULT_HOVER} hint="Menu item hover highlight" />
              </div>
          </Collapsible>

          </>
          )}

          {section === "advanced" && (
          <>
          <Collapsible title="Sign-in screen" subtitle="Custom sign-in background and tagline." badge="Coming soon" defaultOpen onToggle={() => setPreviewMode("signin")}>
              <div className="wl-soon-note">This feature is coming soon. It can't be edited yet.</div>
              <fieldset disabled className="wl-disabled-fs">
              <div className="wl-field" style={{ marginBottom: 18 }}>
                <label className="label">Background</label>
                <div className="wl-seg" role="tablist">
                  <button type="button" className={loginBgType === "color" ? "on" : ""} onClick={() => setLoginBgType("color")}>Solid color</button>
                  <button type="button" className={loginBgType === "image" ? "on" : ""} onClick={() => setLoginBgType("image")}>Image</button>
                </div>
              </div>

              {loginBgType === "color" ? (
                <div className="wl-field" style={{ marginBottom: 18 }}>
                  <ColorField label="Background color" value={loginBgColor} onChange={setLoginBgColor} fallback={DEFAULT_PRIMARY} hint="Fills the left panel of the sign-in page." />
                </div>
              ) : (
                <div className="wl-brand" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 18 }}>
                  <div className="wl-brand-logo">
                    <Dropzone
                      openRef={loginBgRef}
                      onDrop={(files) => upload(files?.[0], setLoginBgUploading, setLoginBgImage)}
                      accept={[MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.webp]}
                      multiple={false}
                      styles={{ root: { border: "none", padding: 0, background: "transparent" } }}
                    >
                      <div className="org-logo wide">
                        {loginBgUploading ? <Loader size="sm" color="#FFBA45" /> : loginBgImage ? <img src={loginBgImage} alt="Sign-in background" /> : <span className="org-logo-empty">Image</span>}
                      </div>
                    </Dropzone>
                    <div className="label" style={{ marginBottom: 0 }}>Background</div>
                  </div>
                  <div className="wl-brand-actions">
                    <button type="button" className="btn sm" onClick={() => loginBgRef.current?.()} disabled={loginBgUploading}>{loginBgImage ? "Change image" : "Upload image"}</button>
                    <div className="field-hint">PNG, JPG or WebP. A wide image works best.</div>
                  </div>
                </div>
              )}

              <div className="wl-field">
                <label className="label">Sign-in tagline</label>
                <input
                  className="input"
                  value={loginTagline}
                  onChange={(e) => setLoginTagline(e.target.value)}
                  placeholder="e.g. Welcome back to Acme Care."
                  maxLength={120}
                />
                <div className="field-hint" style={{ marginTop: 6 }}>Optional subtext under the sign-in heading.</div>
              </div>
              </fieldset>
          </Collapsible>

          <Collapsible title="Email & support" subtitle="Sender identity for transactional emails and your support contact." badge="Coming soon" onToggle={() => setPreviewMode("email")}>
              <div className="wl-soon-note">This feature is coming soon. It can't be edited yet.</div>
              <fieldset disabled className="wl-disabled-fs">
              <div className="wl-color-grid">
                <div className="wl-field">
                  <label className="label">Email sender name</label>
                  <input className="input" value={emailFromName} onChange={(e) => setEmailFromName(e.target.value)} placeholder={brandName || "e.g. Acme Care"} />
                  <div className="field-hint" style={{ marginTop: 6 }}>Shown in the From line. Defaults to your brand name.</div>
                </div>
                <div className="wl-field">
                  <label className="label">Reply-to address</label>
                  <input className="input" value={emailReplyTo} onChange={(e) => setEmailReplyTo(e.target.value)} placeholder="e.g. care@acme.com" />
                  <div className="field-hint" style={{ marginTop: 6 }}>Where replies to your emails go.</div>
                </div>
                <div className="wl-field">
                  <label className="label">Support email</label>
                  <input className="input" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} placeholder="e.g. help@acme.com" />
                </div>
                <div className="wl-field">
                  <label className="label">Support phone</label>
                  <input className="input" value={supportPhone} onChange={(e) => setSupportPhone(e.target.value)} placeholder="e.g. (555) 123-4567" />
                </div>
                <div className="wl-field">
                  <label className="label">Support URL</label>
                  <input className="input" value={supportUrl} onChange={(e) => setSupportUrl(e.target.value)} placeholder="e.g. https://help.acme.com" />
                </div>
              </div>
              </fieldset>
          </Collapsible>

          <div className="card">
            <div className="card-section wl-row">
              <div>
                <div className="card-title">Hide Ogiso logo <span className="wl-soon">Coming soon</span></div>
                <div className="card-subtitle">
                  Remove Ogiso marks from the app chrome and replace them with your brand.
                </div>
                <div className="field-hint" style={{ marginTop: 8 }}>
                  A "Powered by Ogiso" mark is always kept on customer-facing generated artifacts.
                </div>
              </div>
              <Switch on={hideOgisoBranding} onClick={() => setHideOgisoBranding((v) => !v)} disabled />
            </div>
          </div>
          </>
          )}
        </form>

        {/* ───────── Contextual live preview ───────── */}
        <aside className="wl-preview-wrap">
          <div className="card">
            <div className="card-section" style={{ padding: "14px 16px" }}>
              <div className="wl-preview-head">
                Live preview
                <span className="wl-preview-tag">
                  {previewMode === "signin" ? "Sign-in page" : previewMode === "email" ? "Email" : "App"}
                </span>
              </div>

              {/* App chrome preview */}
              {previewMode === "app" && (
                <div className="wl-prev-app">
                  <div className="wl-prev-topbar">
                    <span className="wl-prev-brand">
                      {logoSrc ? <img src={logoSrc} alt="" /> : <span className="wl-prev-logo-fallback" style={{ background: pPrimary }} />}
                      <span className="wl-prev-brandname">{displayBrand}</span>
                    </span>
                  </div>
                  <div className="wl-prev-body">
                    <div className="wl-prev-nav">
                      <span className="wl-prev-nav-item active" style={{ background: normHex(moduleButtonColor, DEFAULT_MODULE), color: pModuleInk }}>Dashboard</span>
                      <span className="wl-prev-nav-item" style={{ background: normHex(hoverAccentColor, DEFAULT_HOVER) }}>Clients</span>
                      <span className="wl-prev-nav-item">Staff</span>
                    </div>
                    <div className="wl-prev-card">
                      <div className="wl-prev-line" style={{ width: "70%" }} />
                      <div className="wl-prev-line" style={{ width: "90%" }} />
                      <button type="button" className="wl-prev-btn" style={{ background: pPrimary, color: pAccent }}>
                        Primary action
                      </button>
                    </div>
                  </div>
                  {!hideOgisoBranding ? <div className="wl-prev-poweredby">Powered by Ogiso</div> : null}
                </div>
              )}

              {/* Sign-in page preview */}
              {previewMode === "signin" && (
                <div className="wl-prev-signin">
                  <div
                    className="wl-prev-signin-side"
                    style={
                      loginBgType === "image" && loginBgImage
                        ? { backgroundImage: `url(${loginBgImage})`, backgroundSize: "cover", backgroundPosition: "center" }
                        : { background: normHex(loginBgColor, DEFAULT_PRIMARY) }
                    }
                  >
                    {logoSrc ? <img src={logoSrc} alt="" /> : <span className="wl-prev-signin-brand" style={{ color: inkOn(normHex(loginBgColor, DEFAULT_PRIMARY)) }}>{displayBrand}</span>}
                  </div>
                  <div className="wl-prev-signin-form">
                    <div className="wl-prev-signin-title">Welcome back</div>
                    <div className="wl-prev-signin-sub">{loginTagline.trim() || (brandName.trim() ? `Sign in to ${brandName.trim()}.` : "Please enter your details to login.")}</div>
                    <div className="wl-prev-input" />
                    <div className="wl-prev-input" />
                    <button type="button" className="wl-prev-btn wl-prev-btn-full" style={{ background: pPrimary, color: pAccent }}>Login</button>
                  </div>
                </div>
              )}

              {/* Email preview */}
              {previewMode === "email" && (
                <div className="wl-prev-email">
                  <div className="wl-prev-email-head">
                    {logoSrc ? <img src={logoSrc} alt="" /> : <span className="wl-prev-brandname">{displayBrand}</span>}
                  </div>
                  <div className="wl-prev-email-from">
                    From: <strong>{emailFromName.trim() || brandName.trim() || "Your brand"}</strong>
                    {emailReplyTo.trim() ? <span className="wl-prev-email-reply"> · replies to {emailReplyTo.trim()}</span> : null}
                  </div>
                  <div className="wl-prev-email-body">
                    <div className="wl-prev-line" style={{ width: "55%" }} />
                    <div className="wl-prev-line" style={{ width: "88%" }} />
                    <div className="wl-prev-line" style={{ width: "80%" }} />
                    <button type="button" className="wl-prev-btn" style={{ background: pPrimary, color: pAccent }}>View in app</button>
                  </div>
                  <div className="wl-prev-email-foot">
                    {supportEmail.trim() ? <div>Need help? {supportEmail.trim()}</div> : null}
                    <div className="wl-prev-poweredby-inline">Powered by Ogiso</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
